import React from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { ProgressTypeRule } from './types';

interface TypeRowProps {
  rule: ProgressTypeRule;
}

const CASE_TYPE_LABELS: Record<string, string> = {
  simple: 'Simple',
  complex: 'Complex',
  psychological: 'Psychology',
};

const humanize = (value: string) =>
  CASE_TYPE_LABELS[value] ??
  value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Row for a single case-type requirement (e.g. "Simple 5/10").
 *
 * Linear progress bar + explicit "N of M" counts, with a success check once
 * the requirement is met.
 */
const TypeRow: React.FC<TypeRowProps> = ({ rule }) => {
  const pct = rule.required === 0
    ? 100
    : Math.min(100, Math.round((rule.approved / rule.required) * 100));
  const label = humanize(rule.case_type);

  return (
    <Box
      sx={{ px: 3, pb: 1.25 }}
      aria-label={`${label}: ${rule.approved} of ${rule.required} approved`}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 500 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
            {rule.approved} / {rule.required}
          </Typography>
          {rule.is_met && (
            <Tooltip title='Requirement met'>
              <CheckCircleRoundedIcon fontSize='small' sx={{ color: '#4caf50' }} />
            </Tooltip>
          )}
        </Box>
      </Box>
      <LinearProgress
        variant='determinate'
        value={pct}
        color={rule.is_met ? 'success' : 'primary'}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { borderRadius: 3 },
        }}
        aria-valuenow={rule.approved}
        aria-valuemin={0}
        aria-valuemax={rule.required}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, lineHeight: 1.3 }}>
          {rule.is_met ? 'Requirement met' : `Need more · ${rule.remaining} to go`}
        </Typography>
      </Box>
    </Box>
  );
};

export default TypeRow;
